import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/** Contractul listelor si paginilor Softsee din /api/v2/softsee/*. */

export const LISTS = ['all', 'workstations', 'servers', 'mobiles'] as const
export type ListName = (typeof LISTS)[number]

export function isList(value: string): value is ListName {
  return (LISTS as readonly string[]).includes(value)
}

export const PAGES = [
  'all_devices',
  'workstations',
  'servers',
  'mobiles',
  'device',
  'applications',
  'licenses',
  'certificates',
  'procedures',
  'installMonitor',
] as const
export type PageName = (typeof PAGES)[number]

export function isPage(value: string): value is PageName {
  return (PAGES as readonly string[]).includes(value)
}

export function listOf(page: string): ListName {
  if (page === 'all_devices') return 'all'
  if (page === 'workstations' || page === 'servers' || page === 'mobiles') return page
  return 'all'
}

export interface TableRequest {
  list: ListName
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

export interface TablePage<T> {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: T[]
}

export function tableParams(r: TableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('devices', r.list)
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function devicesTableQuery(r: TableRequest) {
  return queryOptions({
    queryKey: ['softsee', 'devices', r],
    queryFn: () => apiGet<TablePage<Record<string, unknown>>>(`/softsee/devices-table?${tableParams(r).toString()}`),
  })
}

export function deviceRowQuery(id: number) {
  return queryOptions({
    queryKey: ['softsee', 'device', id],
    queryFn: () => apiGet<Record<string, unknown>>(`/softsee/device/${id}`),
  })
}

function pageParams(r: Omit<TableRequest, 'list'>): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

/**
 * Lista Applications / Licenses poarta si filele randului desfacut pe care omul
 * le are (tabList.php / actionTabs.php le puneau sub `canAccess()`).
 */
export interface TablePageWithTabs<T> extends TablePage<T> {
  tabs?: string[]
}

export function applicationsTableQuery(r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['softsee', 'applications', r],
    queryFn: () => apiGet<TablePageWithTabs<Record<string, unknown>>>(`/softsee/applications-table?${pageParams(r).toString()}`),
  })
}

/**
 * Pe ce USA se cere fisa aplicatiei.
 *
 * E aceeasi fisa si acelasi tabel (`softseeApplications`), dar lista se
 * deschide din doua module, cu doua drepturi. Fiecare usa are capatul ei, ca
 * omul cu Opteemum si fara Softsee sa nu apese pe "+" si sa ia un 403 - vezi
 * `OpteemumPagesController::applicationSummary`.
 */
export type AppDoor = 'softsee' | 'opteemum'

export function applicationSummaryQuery(id: number, door: AppDoor = 'softsee') {
  return queryOptions({
    queryKey: [door, 'application', id, 'summary'],
    queryFn: () =>
      apiGet<{
        details: Record<string, unknown>
        installedVersions: Record<string, unknown>[]
        availableKit: Record<string, unknown> | null
      }>(`/${door}/applications/${id}/summary`),
  })
}

/**
 * Lista statica a masinilor cu aplicatia - numai pe usa Opteemum, care a ramas
 * la `{ data: [...] }`. Pe usa Softsee fila Devices e paginata pe server:
 * `applicationDevicesPageQuery` + `applicationDevicesTableQuery`.
 */
export function applicationDevicesQuery(id: number, door: AppDoor = 'softsee') {
  return queryOptions({
    queryKey: [door, 'application', id, 'devices'],
    queryFn: () => apiGet<{ data: Record<string, unknown>[] }>(`/${door}/applications/${id}/devices`),
  })
}

/** Softsee::getApplicationDevices + filters: versiunile cu numarul de masini, kitul, masinile pentru Update All. */
export interface ApplicationDevicesPage {
  application: { ID: number; short_name: string }
  versions: { version: string; number: number }[]
  kit: { ID: number; name: string; version: string; type: string } | null
  devices: { deviceId: number }[]
}

export function applicationDevicesPageQuery(id: number) {
  return queryOptions({
    queryKey: ['softsee', 'application', id, 'devices-page'],
    queryFn: () => apiGet<ApplicationDevicesPage>(`/softsee/applications/${id}/devices`),
  })
}

/** Un rand din Softsee::devicesSoftseeList (Installed_applications::getTableData). */
export interface ApplicationDeviceRow {
  DT_RowId: string
  ID: number
  version: string | null
  uninstallable: number
  deviceId: number
  Tip: number
  device_name: string | null
  owner_name: string | null
}

export function applicationDevicesTableQuery(
  id: number,
  r: Omit<TableRequest, 'list'> & { version: string },
) {
  const p = pageParams(r)
  p.set('data[versionFilter]', r.version)
  return queryOptions({
    queryKey: ['softsee', 'application', id, 'devices-table', r],
    queryFn: () => apiGet<TablePage<ApplicationDeviceRow>>(`/softsee/applications/${id}/devices-table?${p.toString()}`),
  })
}

export function licensesTableQuery(r: Omit<TableRequest, 'list'>) {
  return queryOptions({
    queryKey: ['softsee', 'licenses', r],
    queryFn: () => apiGet<TablePageWithTabs<Record<string, unknown>>>(`/softsee/licenses-table?${pageParams(r).toString()}`),
  })
}

export function licenseRowsQuery(id: number) {
  return queryOptions({
    queryKey: ['softsee', 'licenses', id, 'rows'],
    queryFn: () => apiGet<{ data: Record<string, unknown>[] }>(`/softsee/licenses/${id}/licenses`),
  })
}

export function licensedDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['softsee', 'licenses', id, 'licensed'],
    queryFn: () => apiGet<{ data: Record<string, unknown>[] }>(`/softsee/licenses/${id}/licensed`),
  })
}

export function unlicensedDevicesQuery(id: number) {
  return queryOptions({
    queryKey: ['softsee', 'licenses', id, 'unlicensed'],
    queryFn: () => apiGet<{ data: Record<string, unknown>[] }>(`/softsee/licenses/${id}/unlicensed`),
  })
}

/** Un rand de registru CMDB: Ci_records::pageFor. */
export interface RegistryRow {
  DT_RowId: string
  ID: number
  name: string
  company_name: string | null
  props: Record<string, unknown>
  due_date: string | null
  due_days: number | null
  due_state: 'none' | 'expired' | 'soon' | 'ok'
}

export interface CertificatesPage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: RegistryRow[]
  class: boolean
  title: string
  blurb: string
  columns: { key: string; label: string; type?: string }[]
  dueLabel: string
  searchHint: string
  /** `REC_CAN_EDIT` din macheta: dreptul de SCRIERE in CMDB, altul decat cel cu care se citeste ecranul. */
  canEdit: boolean
  /** `REC_CLASS_ID`: formularul de adaugare se deschide cu clasa deja aleasa. */
  classId: number
}

export function certificatesQuery(search: string, due: string, start: number, length: number) {
  const p = new URLSearchParams()
  p.set('search[value]', search)
  p.set('due', due)
  p.set('start', String(start))
  p.set('length', String(length))
  return queryOptions({
    queryKey: ['softsee', 'certificates', search, due, start, length],
    queryFn: () => apiGet<CertificatesPage>(`/softsee/certificates-table?${p.toString()}`),
  })
}

export interface ProceduresPage {
  activation: { name: string; version: string; kill: string; line: string }[]
  removal: { name: string; version: string; line: string }[]
  configuration: { name: string; note: string; type: string; category: string; timeout: number; asks: boolean; script: string }[]
  missing: string[]
}

export function proceduresQuery() {
  return queryOptions({
    queryKey: ['softsee', 'procedures'],
    queryFn: () => apiGet<ProceduresPage>('/softsee/procedures'),
  })
}

export interface MonitorOffice {
  ID: number
  Name: string
}

/** Cifrele sunt ale RULARILOR pe statii, nu ale programarilor. */
export interface MonitorPackage {
  package_name: string
  devices: number
  done: number
  failed: number
  expired: number
  pending: number
  replaced?: number
  last_due: string | null
}

export interface MonitorOfficeRow {
  office_name: string
  devices: number
  done: number
  failed: number
  expired: number
  pending: number
}

export interface MonitorProblem {
  ID: number
  device_id: number
  device_name: string
  office_name: string
  package_name: string
  state: number
  due_at: string | null
  note?: string | null
}

export interface MonitorPage {
  offices: MonitorOffice[]
  pickedOffices: number[]
  days: number
  /* Cheia e starea rularii, ca numar (Install_jobs): 1 sent ... 6 replaced. */
  totals: Record<string, number>
  packages: MonitorPackage[]
  byOffice: MonitorOfficeRow[]
  problems: MonitorProblem[]
  /* Numele si lamurirea starii vin de la server, dintr-un singur loc. */
  states: Record<string, { label: string; note: string }>
}

export function installMonitorQuery(offices: number[], days: number) {
  const p = new URLSearchParams()
  p.set('days', String(days))
  offices.forEach((id) => p.append('offices[]', String(id)))
  return queryOptions({
    queryKey: ['softsee', 'install-monitor', offices, days],
    queryFn: () => apiGet<MonitorPage>(`/softsee/install-monitor?${p.toString()}`),
  })
}
